<template>
    <div class="serial-connector column gap4">
        <!-- 标题行 -->
        <div class="serial-header row align-center gap4">
            <span class="serial-dot" :class="statusClass"></span>
            <span class="serial-title">机床连接</span>
        </div>

        <!-- 未连接状态 -->
        <div v-if="status === 'disconnected'" class="column gap4">
            <div class="row gap4 align-center">
                <label class="serial-label">波特率</label>
                <select v-model="baudRate" class="serial-select">
                    <option v-for="b in baudRateOptions" :key="b" :value="b">{{ b }}</option>
                </select>
            </div>
            <button class="serial-btn connect-btn" @click="connect">
                🔌 连接设备
            </button>
            <div v-if="!supported" class="serial-error">
                ⚠ 浏览器不支持<br>请使用 Chrome/Edge
            </div>
        </div>

        <!-- 已连接状态 -->
        <div v-else-if="status === 'connected'" class="column gap4">
            <div class="serial-info">
                <span class="serial-port-name">{{ portInfo }}</span>
            </div>
            <div class="row gap4 align-center">
                <span class="serial-badge">{{ baudRate }}</span>
            </div>
            <button class="serial-btn disconnect-btn" @click="disconnect">
                ⏏ 断开连接
            </button>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="status === 'error'" class="column gap4">
            <div class="serial-error-msg">{{ errorMessage }}</div>
            <button class="serial-btn connect-btn" @click="connect">
                🔄 重试
            </button>
        </div>
    </div>
</template>

<script>
import { ref, computed } from "vue";
import {
    isSerialSupported,
    openSerialPort,
    closeSerialPort,
    formatPortInfo,
} from "../util/serial-port.js";

export default {
    name: "SerialConnector",
    emits: ["connected", "disconnected"],
    setup(_props, { emit }) {
        const supported = ref(isSerialSupported());

        // 状态: 'disconnected' | 'connected' | 'error'
        const status = ref("disconnected");
        const errorMessage = ref("");

        // 串口对象和 reader
        const serialPort = ref(null);
        const serialReader = ref(null);
        const portInfo = ref("");
        const baudRate = ref(9600);

        const baudRateOptions = [4800, 9600, 19200, 38400, 57600, 115200];

        const statusClass = computed(() => {
            return {
                disconnected: "dot-gray",
                connected: "dot-green",
                error: "dot-red",
            }[status.value];
        });

        async function connect() {
            if (!supported.value) {
                status.value = "error";
                errorMessage.value = "浏览器不支持 Web Serial API";
                return;
            }

            try {
                const { port, info } = await openSerialPort({
                    baudRate: baudRate.value,
                });

                serialPort.value = port;
                portInfo.value = formatPortInfo(info);
                status.value = "connected";
                errorMessage.value = "";

                // 把 port 暴露出去
                emit("connected", { port, info });

                // 监听意外断开
                port.addEventListener("disconnect", () => {
                    status.value = "disconnected";
                    serialPort.value = null;
                    serialReader.value = null;
                    portInfo.value = "";
                    emit("disconnected");
                });
            } catch (e) {
                // 用户取消选择不算错误
                if (e.name === "AbortError" || e.message?.includes("cancelled")) {
                    return;
                }
                status.value = "error";
                errorMessage.value = e.message || "连接失败";
                console.error("串口连接失败:", e);
            }
        }

        async function disconnect() {
            try {
                await closeSerialPort(serialPort.value, serialReader.value);
            } catch (e) {
                console.warn("断开串口时出错:", e.message);
            }
            serialPort.value = null;
            serialReader.value = null;
            portInfo.value = "";
            status.value = "disconnected";
            emit("disconnected");
        }

        return {
            supported,
            status,
            errorMessage,
            serialPort,
            portInfo,
            baudRate,
            baudRateOptions,
            statusClass,
            connect,
            disconnect,
        };
    },
};
</script>

<style scoped>
.serial-connector {
    padding: 8px 6px;
    border-bottom: 1px solid #d0d4d7;
    background: #e8ecee;
    font-size: 11px;
}

.serial-header {
    user-select: none;
}

.serial-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}
.dot-gray  { background: #999; }
.dot-green { background: #4caf50; }
.dot-red   { background: #f44336; }

.serial-title {
    font-weight: bold;
    font-size: 12px;
    color: #333;
}

.serial-label {
    font-size: 10px;
    color: #666;
    white-space: nowrap;
}

.serial-select {
    flex: 1;
    font-size: 11px;
    padding: 1px 2px;
    border: 1px solid #bbb;
    border-radius: 2px;
    background: #fff;
    min-width: 0;
}

.serial-btn {
    width: 100%;
    font-size: 11px;
    padding: 4px 6px;
    border: 1px solid #bbb;
    border-radius: 3px;
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.connect-btn {
    background: #4caf50;
    color: #fff;
    border-color: #388e3c;
}
.connect-btn:hover {
    background: #43a047;
}
.disconnect-btn {
    background: #ef5350;
    color: #fff;
    border-color: #c62828;
}
.disconnect-btn:hover {
    background: #e53935;
}

.serial-info {
    text-align: center;
}
.serial-port-name {
    font-size: 10px;
    color: #555;
    word-break: break-all;
}
.serial-badge {
    font-size: 10px;
    color: #fff;
    background: #666;
    padding: 1px 4px;
    border-radius: 2px;
}

.serial-error {
    color: #e65100;
    font-size: 10px;
    text-align: center;
    line-height: 1.3;
}
.serial-error-msg {
    color: #c62828;
    font-size: 10px;
    text-align: center;
    word-break: break-all;
}
</style>
